import { useMemo, useState } from 'react';

import groupBy from 'lodash.groupby';

import type {
  PermissibleRouteKeys,
  PermissionsActions,
  PermissionsResourceType,
} from '@/_pages/settings/user-roles/shared';
import { type SystemUserQuerySchema } from '@/_pages/system-users/shared';
import { useBoolean } from '@/_shared';

export type PermissionsType = Record<
  PermissionsResourceType,
  {
    actions: PermissionsActions[];
    resource: PermissibleRouteKeys;
    resource_type: PermissionsResourceType;
    view: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
    restore: boolean;
  }[]
>;

export const useAuthValues = () => {
  const [user, setUser] = useState<SystemUserQuerySchema | null>(null);
  const dashboardPrivacySettings = useBoolean();

  const permissions = useMemo(() => {
    const allPermissions = (user?.roles || []).map((role) => role.permissions).flat();
    const resourceGrouped = groupBy(allPermissions, 'resource');

    const permissions = groupBy(
      Object.keys(resourceGrouped).map((key) => {
        const ps = resourceGrouped[key] || [];
        const actions = ps.map((p) => p.action);
        return {
          actions,
          resource: key,
          resource_type: ps?.[0]?.resource_type,
          view: actions.includes('view'),
          create: actions.includes('create'),
          update: actions.includes('update'),
          delete: actions.includes('delete'),
          restore: actions.includes('revert'),
        };
      }),
      'resource_type'
    ) as PermissionsType;

    return permissions;
  }, [user]);

  return useMemo(() => {
    return {
      user,
      setUser,
      permissions,
      dashboardPrivacySettings,
    };
  }, [permissions, user, dashboardPrivacySettings]);
};

export type UseAuthValues = ReturnType<typeof useAuthValues>;
